import { useEffect, useMemo, useState } from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import ClientLogo from '@/components/ClientLogo/ClientLogo';
import { useClientsQuery, type ClientSummary } from '@/features/Clients/Data';

export interface ClientComboboxProps {
  selectedId?: string | null;
  onChange: (client: ClientSummary | null) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyLabel?: string;
}

export function ClientCombobox({
  selectedId,
  onChange,
  disabled,
  placeholder = 'Selecione um cliente',
  emptyLabel = 'Nenhum cliente encontrado.',
}: ClientComboboxProps) {
  const { data: clients = [], isLoading, error } = useClientsQuery();
  const [query, setQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(null);

  useEffect(() => {
    if (!selectedId) {
      setSelectedClient(null);
      return;
    }

    // Avoid updating while clients still loading (unless no clients)
    if (clients.length === 0) {
      return;
    }

    const found = clients.find((client) => client.id === selectedId) ?? null;
    setSelectedClient(found);
  }, [selectedId, clients]);

  const filteredClients = useMemo(() => {
    if (!query) return clients;
    const lowerQuery = query.toLowerCase();
    return clients.filter((client) => client.name?.toLowerCase().includes(lowerQuery));
  }, [clients, query]);

  const handleSelectionChange = (client: ClientSummary | null) => {
    setSelectedClient(client);
    onChange(client);
  };

  if (isLoading) {
    return <div className="input-primary mt-1 w-full text-gray-500">Carregando clientes...</div>;
  }

  if (error) {
    return (
      <div className="input-primary mt-1 w-full text-red-600">
        Não foi possível carregar a lista de clientes.
      </div>
    );
  }

  return (
    <Combobox
      as="div"
      value={selectedClient}
      onChange={handleSelectionChange}
      disabled={disabled}
      className="mt-1"
    >
      <div className="relative">
        <ComboboxInput
          className="input-primary w-full"
          displayValue={(client: ClientSummary | null) => client?.name ?? ''}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          autoComplete="off"
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </ComboboxButton>

        <Transition
          afterLeave={() => setQuery('')}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredClients.length === 0 ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                {emptyLabel}
              </div>
            ) : (
              filteredClients.map((client) => (
                <ComboboxOption
                  key={client.id}
                  value={client}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-12 pr-4 ${
                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                    }`
                  }
                >
                  {({ active, selected }) => (
                    <>
                      <div className="flex items-center">
                        <ClientLogo client={client} clientId={client.id} variant={32} className="h-8 w-8" />
                        <span className={`ml-3 block truncate ${selected ? 'font-semibold' : ''}`}>
                          {client.name}
                        </span>
                      </div>
                      {selected && (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? 'text-white' : 'text-indigo-600'
                          }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </Transition>
      </div>
    </Combobox>
  );
}

export default ClientCombobox;
